import React, { useCallback, useMemo } from "react";

import { FONT_FAMILY, DEFAULT_FONT_SIZE } from "@excalidraw/common";

import type { FontFamilyValues, ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "../../types";
import type { AppClassProperties } from "../../types";

import { t } from "../../i18n";
import { RadioSelection } from "../RadioSelection";
import { ButtonSeparator } from "../ButtonSeparator";
import {
  FontSizeSmallIcon,
  FontSizeMediumIcon, 
  FontSizeLargeIcon,
  FontSizeExtraLargeIcon,
} from "../icons";
import MultiFontSize from "../Stats/MultiFontSize";
import { isTextElement } from "@excalidraw/element/typeChecks";
import { getBoundTextElement } from "@excalidraw/element";

import { LofiFontPickerWrapper } from "./LofiFontPickerWrapper";

import "./FontPicker.scss";

const DEFAULT_FONT_SIZES = [
  {
    value: 16,
    text: t("labels.small"),
    icon: FontSizeSmallIcon,
    testId: "fontSize-small",
  },
  {
    value: 20,
    text: t("labels.medium"),
    icon: FontSizeMediumIcon,
    testId: "fontSize-medium",
  },
  {
    value: 28,
    text: t("labels.large"),
    icon: FontSizeLargeIcon,
    testId: "fontSize-large",
  },
  {
    value: 36,
    text: t("labels.veryLarge"),
    icon: FontSizeExtraLargeIcon,
    testId: "fontSize-veryLarge",
  },
];

// Since we removed the default font buttons, this always returns false
export const isDefaultFont = (fontFamily: number | null) => {
  return false;
};

interface FontPickerProps {
  isOpened: boolean;
  selectedFontFamily: FontFamilyValues | null;
  onSelect: (fontFamily: FontFamilyValues) => void;
  onPopupChange: (open: boolean) => void;
  // Font size props
  selectedElements: readonly ExcalidrawElement[];
  appState: AppState;
  app: AppClassProperties;
  onFontSizeChange: (fontSize: number) => void;
}

export const FontPicker = React.memo(
  ({
    isOpened,
    selectedFontFamily,
    onSelect,
    onPopupChange,
    selectedElements,
    appState,
    app,
    onFontSizeChange,
  }: FontPickerProps) => {
    const defaultFontSizes = useMemo(() => DEFAULT_FONT_SIZES, []);
    const onSelectCallback = useCallback(
      (value: number | false) => {
        if (value) {
          onSelect(value);
        }
      },
      [onSelect],
    );

    const onFontSizeCallback = useCallback(
      (value: number | false, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (value) {
          onFontSizeChange(value);
        }
      },
      [onFontSizeChange],
    );

    // Get current font size for selected elements
    const currentFontSize = useMemo(() => {
      let fontSize: number | null = null;
      
      // If we're editing text, use that element's font size
      if (appState.editingTextElement && isTextElement(appState.editingTextElement)) {
        fontSize = appState.editingTextElement.fontSize;
      }
      // If we have selected elements, check their font sizes
      else if (selectedElements.length > 0) {
        const fontSizes = selectedElements
          .map((element) => {
            if (isTextElement(element)) {
              return element.fontSize;
            }
            const boundTextElement = getBoundTextElement(
              element,
              app.scene.getNonDeletedElementsMap(),
            );
            return boundTextElement ? boundTextElement.fontSize : null;
          })
          .filter(Boolean);
        
        const uniqueSizes = new Set(fontSizes);
        // If all selected elements have the same font size, use that
        if (uniqueSizes.size === 1) {
          fontSize = fontSizes[0];
        }
        // If they have different sizes, don't highlight any button
        else if (uniqueSizes.size > 1) {
          return null;
        }
      }
      else {
        // Fall back to current item font size from app state
        fontSize = appState.currentItemFontSize || DEFAULT_FONT_SIZE;
      }
      
      // Check if the fontSize matches one of our predefined sizes
      const predefinedSizes = [16, 20, 28, 36];
      if (fontSize && predefinedSizes.includes(fontSize)) {
        return fontSize;
      }
      
      // If it's a custom size, return null to not highlight any button
      return null;
    }, [selectedElements, appState, app]);

    return (
      <div role="dialog" aria-modal="true" className="FontPicker__container">
        <LofiFontPickerWrapper
          isOpened={isOpened}
          selectedFontFamily={selectedFontFamily}
          onSelect={onSelectCallback}
          onPopupChange={onPopupChange}
        />
        <ButtonSeparator />
        <div className="buttonList">
          <RadioSelection<number | false>
            type="button"
            options={defaultFontSizes}
            value={currentFontSize}
            onClick={onFontSizeCallback}
          />
          {selectedElements.length > 0 && (
            <div className="font-size-text-container">
              <MultiFontSize
                property="fontSize"
                elements={selectedElements}
                scene={app.scene}
                appState={appState}
                elementsMap={app.scene.getNonDeletedElementsMap()}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.isOpened === next.isOpened &&
    prev.selectedFontFamily === next.selectedFontFamily &&
    prev.selectedElements === next.selectedElements &&
    prev.appState.currentItemFontSize === next.appState.currentItemFontSize,
);
